import { useTranslation } from "@shared/utils/internationalization";
import Box from "@ui-kit/layout/Box/Box.web";
import Button from "@ui-kit/form/Button/Button.web";
import CDNImage from "@ui-kit/media/CDNImage/CDNImage.web";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import Text from "@ui-kit/typography/Text/Text.web";
import { formatRelativeStaticPath } from "@shared/utils/images/utils";
import useDetailPageScrollMargin from "@app-contexts/public/features/event-detail/hooks/useDetailPageScrollMargin";
import type { AccommodationsSharedProps } from "./Accommodations.props";

export default function Accommodations(
  props: AccommodationsProps
): JSX.Element {
  const { url } = props;
  const { t } = useTranslation("purchases");
  const scrollMargin = useDetailPageScrollMargin();

  return (
    <Box
      data-testid="accomodations-container"
      id="accommodations"
      scrollMarginTop={scrollMargin}
      backgroundColor="primary.900"
      _hover={{ backgroundColor: "primary.800" }}
      p="none"
      overflow="hidden"
      borderRadius={{ base: "none", sm: "lg" }}
    >
      <Flex
        direction={{ base: "column", xs: "row" }}
        // TODO: Create HoverEffectContainer component for complex transform animations
        // eslint-disable-next-line showpass-custom/prefer-semantic-props
        css={{
          "&:hover div:nth-of-type(2) > div:nth-of-type(1)": {
            transform: "translateX(20%) rotate(-35deg)",
          },
          "&:hover div:nth-of-type(2) > div:nth-of-type(2)": {
            transform: "translateX(20%) rotate(-25deg)",
          },
          "&:hover div:nth-of-type(2) > div:nth-of-type(3)": {
            transform: "translateX(20%) rotate(15deg)",
          },
        }}
      >
        <Box
          w={{ base: "100%", xs: "60%" }}
          p="md"
          zIndex={4}
          backgroundColor="inherit"
        >
          {/* <Box
            backgroundColor="purple.200"
            p="4px 8px"
            borderRadius="2px"
            w="max-content"
            mb="4px"
          >
            <Text color="purple.900" fontSize="12px" fontWeight="600">
              {t("New")}
            </Text>
          </Box> */}
          <Text
            mb="xs"
            fontSize={{ base: "md", sm: "lg" }}
            fontWeight="bold"
            color="bg"
          >
            {t("Accommodations")}
          </Text>
          <Text fontSize={{ base: "xs", sm: "sm" }} color="bg" mb="3xl">
            {t(
              "We've partnered with a leading hotel provider to bring you exclusive accommodation deals. Access discounted rates at top hotels when you book through our dedicated link."
            )}
          </Text>
          <Button
            data-testid="event-detail-accommodations"
            variant="outline"
            colorPalette="light"
            onClick={() =>
              window.open(url || "", "_blank", "noopener,noreferrer")
            }
            _hover={{ color: "secondary-800" }}
            w={{ base: "100%", xs: "max-content" }}
          >
            {t("Book now")}
          </Button>
        </Box>

        {/* Images Container */}
        <Box
          w={{ base: "100%", xs: "50%" }}
          position="relative"
          overflow="visible"
          mt={{ base: "md", xs: "none" }}
          display={{ base: "flex", xs: "block" }}
          flexDirection={{ base: "column", xs: "row" }}
          alignItems="center"
          justifyContent="center"
          // TODO: Add element token for 150px (between element.8xl=128px and element.9xl=160px)
          // eslint-disable-next-line showpass-custom/no-hardcoded-style-values
          h={{ base: "150px", xs: "auto" }}
        >
          <Box
            position="absolute"
            top={{ base: "0%", xs: "20%" }}
            left={{ base: "10%", xs: "0%" }}
            // TODO: Add transform prop for rotations or create RotatedBox component
            // eslint-disable-next-line showpass-custom/prefer-semantic-props
            css={{
              transform: "rotate(-20.69deg)",
            }}
            transition="transform 0.3s ease"
          >
            <CDNImage
              alt=""
              decorative
              src={formatRelativeStaticPath(
                "/images/discover/accomodations_image_1.jpg"
              )}
              width={190}
              height={190}
              style={{
                zIndex: 1,
                borderRadius: "12px",
                objectFit: "cover", // Ensures the image fills the container nicely
              }}
            />
          </Box>

          <Box
            position="absolute"
            top={{ base: "10%", xs: "30%" }}
            left={{ base: "30%", xs: "25%" }}
            // TODO: Add transform prop for rotations or create RotatedBox component
            // eslint-disable-next-line showpass-custom/prefer-semantic-props
            css={{
              transform: "rotate(-8.69deg)",
            }}
            transition="transform 0.3s ease"
          >
            <CDNImage
              alt=""
              decorative
              src={formatRelativeStaticPath(
                "/images/discover/accomodations_image_2.jpg"
              )}
              width={190}
              height={190}
              style={{
                zIndex: 2,
                borderRadius: "12px",
                objectFit: "cover",
              }}
            />
          </Box>

          <Box
            position="absolute"
            top={{ base: "20%", xs: "45%" }}
            left={{ base: "50%", xs: "45%" }}
            // TODO: Add transform prop for rotations or create RotatedBox component
            // eslint-disable-next-line showpass-custom/prefer-semantic-props
            css={{
              transform: "rotate(7.85deg)",
            }}
            transition="transform 0.3s ease"
          >
            <CDNImage
              alt=""
              decorative
              src={formatRelativeStaticPath(
                "/images/discover/accomodations_image_3.jpg"
              )}
              width={190}
              height={190}
              style={{
                zIndex: 3,
                borderRadius: "12px",
                objectFit: "cover",
              }}
            />
          </Box>
        </Box>
      </Flex>
    </Box>
  );
}

export type AccommodationsProps = AccommodationsSharedProps & {
  // Add any web specific props here.
};
